import { CommonModule } from "@angular/common";
import {
  Component,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  inject,
  AfterViewInit,
} from "@angular/core";

import { MatTooltipModule } from "@angular/material/tooltip";
import {
  ApexChart,
  ApexDataLabels,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexTooltip,
  ChartComponent,
  NgApexchartsModule,
} from "ng-apexcharts";
import { ProviderCaseStatusGetDTO } from "src/app/pages/providers/provider.model";
import { FiledClaimStatusSumaryUiModel } from "../../models/filed-case-ui.model";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DashboardChartEmptyStateComponent } from "../../shared/dashboard-chart-empty-state/dashboard-chart-empty-state.component";

export type ChartOptions = {
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: any;
  colors?: string[];
  legend?: ApexLegend;
  tooltip?: ApexTooltip;
  plotOptions: ApexPlotOptions;
  stroke?: any;
  dataLabels?: ApexDataLabels;
};

@Component({
  selector: "app-claim-status-chart",
  standalone: true,
  imports: [
    NgApexchartsModule,
    CommonModule,

    MatTooltipModule,
    TranslateModule,
    DashboardChartEmptyStateComponent,
  ],
  templateUrl: "./claim-status-chart.component.html",
  styleUrls: ["./claim-status-chart.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ClaimStatusChartComponent implements AfterViewInit {
  @ViewChild("chart") chart: ChartComponent;

  public chartOptions: ChartOptions;
  private readonly translate = inject(TranslateService);

  @Input({ required: true }) statusSummary: FiledClaimStatusSumaryUiModel = {};
  @Input({ required: true }) statusesApi: ProviderCaseStatusGetDTO[] = [];
  @Input({ required: true }) successRate: number = 0;

  @Output() readonly statusSelected = new EventEmitter<string>();

  get hasData(): boolean {
    return Object.keys(this.statusSummary || {}).length > 0;
  }

  statuses: string[] = [];
  private statusKeys: string[] = [];
  legendItems: {
    label: string;
    color: string;
    statusValue: string;
    total: number;
  }[] = [];
  private mutationObserver: MutationObserver;

  constructor() {
    this.chartOptions = {
      series: Object.values(this.statusSummary).map((status) => status.total),
      chart: {
        type: "donut",
        width: 150,
        height: 200,
        events: {
          dataPointSelection: (
            _event: unknown,
            _chartContext: unknown,
            config: { dataPointIndex: number },
          ) => {
            this.onChartSliceSelected(config.dataPointIndex);
          },
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "85%",
            labels: this.buildDonutCenterLabels("#777"),
          },
        },
      },
      labels: this.statuses,
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
        lineCap: "round",
      },
      dataLabels: {
        enabled: false,
        style: {
          colors: ["#2a3547"],
          fontWeight: "bold",
          fontSize: "12px",
        },
      },
      tooltip: {
        enabled: true,
        custom: ({ seriesIndex, w }: any) => {
          const statusKeys = Object.keys(this.statusSummary || {});
          const key = statusKeys[seriesIndex] ?? "";
          const status = this.statusSummary[key];
          if (!status) return "";

          const label = this.getStatusLabel(key);
          const total = status.total ?? 0;
          const amount = status.amount ?? 0;
          const cashReimb = status.cashReimbursementAmount ?? 0;
          const inventoryReimb = status.inventoryReimbursementAmount ?? 0;

          const amountFormatted = new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }).format(amount);

          const cashReimbFormatted = new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }).format(cashReimb);

          const inventoryReimbFormatted = new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 2,
          }).format(inventoryReimb);

          const color = w.config.colors?.[seriesIndex] || "#1972c5";

          // Get translated labels
          const totalLabel = this.translate.instant("dashboard.tooltip.total");
          const amountLabel = this.translate.instant(
            "dashboard.tooltip.amount"
          );
          const cashReimbLabel = this.translate.instant(
            "dashboard.tooltip.cash_reimb"
          );
          const inventoryReimbLabel = this.translate.instant(
            "dashboard.tooltip.inventory_reimb"
          );

          return (
            `<div style="padding:10px;font-size:12px;background:${color};border-radius:6px;box-shadow:0 4px 12px rgba(0,0,0,0.15)">` +
            `<div style="color:#fff;font-weight:700;margin-bottom:6px">${label}</div>` +
            `<div style="color:#fff;margin-bottom:4px"><span>${totalLabel}:</span> <strong>${total}</strong></div>` +
            `<div style="color:#fff;margin-bottom:4px"><span>${amountLabel}:</span> <strong>${amountFormatted}</strong></div>` +
            `<div style="color:#fff;margin-bottom:4px"><span>${cashReimbLabel}:</span> <strong>${cashReimbFormatted}</strong></div>` +
            `<div style="color:#fff"><span>${inventoryReimbLabel}:</span> <strong>${inventoryReimbFormatted}</strong></div>` +
            `</div>`
          );
        },
      },
      responsive: [
        {
          breakpoint: 600,
          options: {
            chart: {
              //width: 200,
            },
            legend: {
              position: "bottom",
            },
          },
        },
      ],
      legend: {
        show: false,
        position: "bottom",
        //horizontalAlign: "center",
        labels: {
          colors: "#2a3547",
        },
        // itemMargin: {
        //   horizontal: 0,
        //   vertical: 5,
        // },
        formatter: function (val, opts) {
          return val;
        },
      },
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["statusSummary"] ||
      changes["statusesApi"] ||
      changes["successRate"]
    ) {
      this.updateSeries();
      this.syncDonutCenterLabels();
      this.updateColorsFromTheme();
    }
  }

  ngOnInit(): void {
    this.syncDonutCenterLabels();
    this.updateColorsFromTheme();

    this.mutationObserver = new MutationObserver(() => {
      this.updateColorsFromTheme();
      this.updateSeries();
    });

    this.mutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    this.updateSeries();

    this.translate.onLangChange.subscribe(() => {
      this.syncDonutCenterLabels();
      this.updateSeries();
      this.updateColorsFromTheme();
    });
  }

  ngAfterViewInit(): void {
    this.syncDonutCenterLabels();
    this.updateColorsFromTheme();
  }

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  private isDarkMode(): boolean {
    return document.documentElement.classList.contains("dark-theme");
  }

  private buildDonutCenterLabels(centerTextColor: string) {
    return {
      show: true,
      name: {
        show: true,
        fontSize: "9px",
        fontWeight: "600",
        color: centerTextColor,
        offsetY: -8,
      },
      value: {
        show: true,
        fontSize: "14px",
        fontWeight: "700",
        color: centerTextColor,
        offsetY: 4,
      },
      total: {
        show: true,
        showAlways: true,
        label: this.translate.instant("dashboard.success_rate_label"),
        fontSize: "9px",
        fontWeight: "600",
        color: centerTextColor,
        formatter: () => `${Number(this.successRate.toFixed(2))}%`,
      },
    };
  }

  private syncDonutCenterLabels(): void {
    const centerTextColor = this.isDarkMode() ? "#fff" : "#000";

    if (!this.chartOptions.plotOptions?.pie?.donut) {
      this.chartOptions.plotOptions = {
        pie: {
          donut: {
            size: "85%",
            labels: this.buildDonutCenterLabels(centerTextColor),
          },
        },
      };
      return;
    }

    this.chartOptions.plotOptions.pie.donut.labels =
      this.buildDonutCenterLabels(centerTextColor);
  }

  private getStatusLabel(statusValue: string): string {
    const translationKey = `caseStatuses.${statusValue}`;
    const translated = this.translate.instant(translationKey);

    if (translated !== translationKey) {
      return translated;
    }

    const match = this.statusesApi.find(
      (status) =>
        status.value.toLocaleLowerCase() === statusValue.toLocaleLowerCase(),
    );

    return match?.name ?? statusValue;
  }

  private updateSeries(): void {
    const statusKeys = Object.keys(this.statusSummary);
    this.statusKeys = statusKeys;

    this.statuses = statusKeys.map((key) => this.getStatusLabel(key));

    this.chartOptions.series = statusKeys.map((s) => {
      const status = this.statusSummary[s];
      return status ? status.total : 0;
    });

    this.chartOptions.labels = this.statuses;

    // Build legend items with synchronized colors
    this.legendItems = statusKeys.map((key, index) => {
      const label = this.getStatusLabel(key);
      const color =
        this.chartOptions.colors?.[index % this.chartOptions.colors.length] ??
        "#ccc";
      const total = this.statusSummary[key]?.total ?? 0;

      return { label, color, statusValue: key, total };
    });
  }

  onLegendItemClick(statusValue: string): void {
    this.statusSelected.emit(this.resolveStatusValue(statusValue));
  }

  private onChartSliceSelected(dataPointIndex: number): void {
    const statusValue = this.statusKeys[dataPointIndex];
    if (statusValue) {
      this.onLegendItemClick(statusValue);
    }
  }

  private resolveStatusValue(statusValue: string): string {
    const match = this.statusesApi.find(
      (status) =>
        status.value.toLowerCase() === statusValue.toLowerCase(),
    );

    return match?.value ?? statusValue;
  }

  readonly legendLabelMaxLength = 40;

  truncateLegendLabel(label: string): string {
    if (label.length <= this.legendLabelMaxLength) {
      return label;
    }

    return `${label.slice(0, this.legendLabelMaxLength - 1)}…`;
  }

  getLegendItemTooltip(label: string): string {
    return this.translate.instant("dashboard.view_cases_with_status", {
      status: label,
    });
  }

  private updateColorsFromTheme(): void {
    const root = getComputedStyle(document.documentElement);

    const baseColors: string[] = [];
    const maxColors = 40;

    for (let i = 1; i <= maxColors; i++) {
      const varName = `--mat-charts-color-${this.numberToWord(i)}`;
      const color = root.getPropertyValue(varName).trim();
      if (color) {
        baseColors.push(color);
      }
    }

    const statusCount = Object.keys(this.statusSummary).length;
    const colors = Array.from(
      { length: statusCount },
      (_, i) => baseColors[i % baseColors.length]
    );

    this.chartOptions.colors = colors;

    this.syncDonutCenterLabels();

    const isDark = this.isDarkMode();

    this.chartOptions.legend = {
      show: false,
      position: "right",
      labels: {
        colors: isDark ? "#fff" : "#2a3547",
      },
      itemMargin: {
        horizontal: 0,
        vertical: 5,
      },
      formatter: function (val, opts) {
        return val;
      },
    };

    if (this.chart?.updateOptions) {
      this.chart.updateOptions({
        colors: this.chartOptions.colors,
        legend: this.chartOptions.legend,
        plotOptions: this.chartOptions.plotOptions,
      });
    }
  }

  // calculateOpenSuccessRate(): number {
  //   if (Object.keys(this.statusSummary).length === 0) return 0;

  //   // Total cases (closed + non-closed)
  //   const totalCases = Object.values(this.statusSummary).reduce(
  //     (sum, status) => sum + (status.total ?? 0),
  //     0
  //   );

  //   // Total de casos NO cerrados
  //   const nonClosedCases = Object.values(this.statusSummary)
  //     .filter((status) => !status.isClosed)
  //     .reduce((sum, status) => sum + (status.total ?? 0), 0);

  //   if (totalCases === 0) return 0;
  //   return (nonClosedCases / totalCases) * 100;
  // }

  // Helper function to convert numbers to words (1 -> 'one', 2 -> 'two', etc.)
  private numberToWord(num: number): string {
    const words = [
      "one",
      "two",
      "three",
      "four",
      "five",
      "six",
      "seven",
      "eight",
      "nine",
      "ten",
      "eleven",
      "twelve",
      "thirteen",
      "fourteen",
      "fifteen",
      "sixteen",
      "seventeen",
      "eighteen",
      "nineteen",
      "twenty",
      "twentyone",
      "twentytwo",
      "twentythree",
      "twentyfour",
      "twentyfive",
      "twentysix",
      "twentyseven",
      "twentyeight",
      "twentynine",
      "thirty",
      "thirtyone",
      "thirtytwo",
      "thirtythree",
      "thirtyfour",
      "thirtyfive",
      "thirtysix",
      "thirtyseven",
      "thirtyeight",
      "thirtynine",
      "forty",
    ];
    return words[num - 1] || "";
  }
}
